import React, { useState } from 'react';
import { Phone, MessageCircle, Mail } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useStore } from '../../store/useStore';
import { leadService } from '../../lib/leadService';
import { whatsappUrl } from '../../lib/phone';
import { Lead } from '../../types/leads.types';

interface LeadQuickActionsProps {
    lead: Lead;
    onLogged: () => void; // ricarica dopo aver loggato (aggiorna timeline/contatore)
    onEmail?: () => void; // se passato, "Email" apre questo (es. modale invio) invece di mailto
    /** compatto: pensato per l'hover sulle card kanban/lista (ferma drag e click) */
    compact?: boolean;
    className?: string;
}

// Registra un contatto in 1 click: l'evento 'done' azzera il contatore "giorni fermo".
export function LeadQuickActions({ lead, onLogged, onEmail, compact, className }: LeadQuickActionsProps) {
    const { addNotification } = useStore();
    const [busy, setBusy] = useState<string | null>(null);

    const stop = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    const run = async (key: string, fn: () => Promise<void>) => {
        setBusy(key);
        try {
            await fn();
            onLogged();
        } catch (error) {
            console.error('quick action error:', error);
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile registrare il contatto' });
        } finally {
            setBusy(null);
        }
    };

    const handleCall = () =>
        run('call', async () => {
            await leadService.logActivity(lead.id, 'call', 'Chiamata');
            addNotification({ type: 'success', title: 'Chiamata registrata' });
        });

    const handleWhatsapp = () =>
        run('whatsapp', async () => {
            const url = whatsappUrl(lead.phone);
            if (url) window.open(url, '_blank', 'noopener');
            await leadService.logActivity(lead.id, 'whatsapp', 'Messaggio WhatsApp');
            addNotification({ type: 'success', title: 'WhatsApp registrato' });
        });

    const handleEmail = () => {
        if (onEmail) {
            onEmail();
            return;
        }
        run('email', async () => {
            if (lead.email) window.open(`mailto:${lead.email}`, '_blank');
            await leadService.logActivity(lead.id, 'email', 'Email');
            addNotification({ type: 'success', title: 'Email registrata' });
        });
    };

    const btn = cn(
        'flex items-center gap-1 rounded-md border font-medium transition-colors disabled:opacity-50',
        compact ? 'px-2 py-1.5 md:px-1.5 md:py-1 text-[11px] bg-white/90' : 'px-2.5 py-1.5 text-xs'
    );

    return (
        <div
            className={cn('flex items-center gap-1', className)}
            onPointerDown={stop}
            onClick={stop}
        >
            <button
                type="button"
                title="Registra chiamata"
                disabled={busy === 'call'}
                onClick={handleCall}
                className={cn(btn, 'text-blue-700 border-blue-200 hover:bg-blue-50')}
            >
                <Phone className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {!compact && 'Chiamata'}
            </button>
            <button
                type="button"
                title={lead.phone ? 'Apri WhatsApp e registra' : 'Nessun telefono'}
                disabled={busy === 'whatsapp' || !lead.phone}
                onClick={handleWhatsapp}
                className={cn(btn, 'text-emerald-700 border-emerald-200 hover:bg-emerald-50')}
            >
                <MessageCircle className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {!compact && 'WhatsApp'}
            </button>
            <button
                type="button"
                title={lead.email ? 'Email' : 'Nessuna email'}
                disabled={busy === 'email' || (!onEmail && !lead.email)}
                onClick={handleEmail}
                className={cn(btn, 'text-amber-700 border-amber-200 hover:bg-amber-50')}
            >
                <Mail className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {!compact && 'Email'}
            </button>
        </div>
    );
}

export default LeadQuickActions;
